import React, { createContext, useContext, useState, useEffect } from 'react';
import apiClient from '../api/apiClient';

const AuthContext = createContext(null);

/** Normaliser bruger fra API/localStorage til både nye og eksisterende felter */
function normalizeUser(user) {
  if (!user) return null;
  return {
    ...user,
    id: user.bruger_id ?? user.id,
    bruger_id: user.bruger_id ?? user.id,
    role: user.rettighed ?? user.role,
    rettighed: user.rettighed ?? user.role,
    scId: user.sprogcenter_id ?? user.scId,
    sprogcenter_id: user.sprogcenter_id ?? user.scId,
  };
}

/** Normaliser kontekst-valg fra login (context switcher) */
function normalizeContext(ctx) {
  return {
    ...ctx,
    accessId: ctx.rettigheder_id ?? ctx.accessId,
    rettigheder_id: ctx.rettigheder_id ?? ctx.accessId,
    rettighed: ctx.rettighed ?? ctx.role,
    role: ctx.rettighed ?? ctx.role,
    sprogcenter_id: ctx.sprogcenter_id ?? ctx.scId,
    scId: ctx.sprogcenter_id ?? ctx.scId,
  };
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('authToken');
    const savedUser = localStorage.getItem('userData');
    if (token && savedUser) {
      setUser(normalizeUser(JSON.parse(savedUser)));
    }
  }, []);

  const login = async (email, password, accessId = null) => {
    try {
      setError(null);
      const response = await apiClient.post('/api/auth/login', { email, password, accessId });

      if (response.data.requireContextSelection) {
        return {
          requiresSelection: true,
          contexts: (response.data.contexts || []).map(normalizeContext),
        };
      }

      if (response.data.token) {
        const normalizedUser = normalizeUser(response.data.user);
        localStorage.setItem('authToken', response.data.token);
        localStorage.setItem('userData', JSON.stringify(normalizedUser));
        setUser(normalizedUser);
        return { success: true };
      }
    } catch (err) {
      console.error('Login error', err);
      setError(err.response?.data?.message || 'Fejl ved login');
      return { success: false };
    }
  };

  const logout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('userData');
    setUser(null);
  };

  const updateUser = (userData) => {
    setUser((prev) => {
      const newUser = normalizeUser({ ...prev, ...userData });
      localStorage.setItem('userData', JSON.stringify(newUser));
      return newUser;
    });
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, updateUser, error }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
